import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Document from "documents/document";
import DocumentName from "documents/names/document-name";
import DocumentTopic from "documents/topics/document-topic";
import { DocumentFileType } from "documents/document-file-type";
import { JSONType } from "work/proposals/redlining/field-redlines/field-redline";
import WorkDocumentSchema, { WorkDocumentJSON, WorkDocumentParams } from "work/work-agreements/contracts/_schemas/work-document-schema";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";

export default class WorkDocument extends Document {
  public static get EXAMPLE(): WorkDocument {
    return new WorkDocument({
      creatorId: Guid.generate(),
      id: Guid.generate(),
      fileType: DocumentFileType.PDF,
      hasSystemPermissions: false,
      readAccess: [Guid.generate()],
      writeAccess: [Guid.generate()],
      name: new DocumentName("Example Work Document"),
      documentType: WorkDocumentType.ClientPolicy,
      created: new AHDate(),
      fileSize: 1024,
      replacesId: Guid.generate(),
      templateIds: [Guid.generate()],
      topics: [DocumentTopic.EXAMPLE],
    });
  }

  public static create(values: WorkDocumentParams): WorkDocument {
    const parsed = WorkDocumentSchema.validate(values);
    return new WorkDocument(parsed);
  }

  public static fromDocument(
    document: Document,
    documentType: WorkDocumentType,
    replacesId?: Guid,
    templateIds?: Guid[]
  ) {
    return WorkDocument.create({
      id: document.id,
      creatorId: document.creatorId,
      fileSize: document.fileSize,
      fileType: document.fileType,
      created: document.created,
      readAccess: document.readAccess,
      writeAccess: document.writeAccess,
      hasSystemPermissions: document.hasSystemPermissions,
      name: document.name,
      topics: document.topics,
      templateIds: templateIds ?? document.templateIds,
      documentType,
      replacesId,
    });
  }

  public static override fromJSON(json: WorkDocumentJSON): WorkDocument {
    const id = Guid.fromJSON(json.id);
    const name = DocumentName.fromJSON(json.name);
    if (!id)
      throw new Error("Document Id is required");
    if (!name)
      throw new Error("Document Name is required");

    return new WorkDocument({
      ...json,
      id,
      name,
      creatorId: Guid.fromJSON(json.creatorId) ?? undefined,
      documentType: json.documentType 
        ? WorkDocumentType[json.documentType as keyof typeof WorkDocumentType] 
        : undefined,
      fileSize: json.fileSize ?? undefined,
      fileType: json.fileType,
      created: AHDate.fromJSON(json.created) ?? undefined,
      hasSystemPermissions: Boolean(json.hasSystemPermissions),
      readAccess: json.readAccess?.map((id) => {
        const guid = Guid.fromJSON(id);
        if (!guid)
          throw new Error("Invalid read access GUID");
        return guid;
      }),
      writeAccess: json.writeAccess?.map((id) => {
        const guid = Guid.fromJSON(id);
        if (!guid)
          throw new Error("Invalid write access GUID");
        return guid;
      }),
      topics: json.topics?.map((topic) => {
        const result = DocumentTopic.fromJSON(topic);
        if (!result)
          throw new Error("Invalid topic");
        return result;
      }) ?? [],
      templateIds: json.templateIds?.map((id) => {
        const guid = Guid.fromJSON(id);
        if (!guid)
          throw new Error("Invalid template ID");
        return guid;
      }) ?? [],
      replacesId: Guid.fromJSON(json.replacesId) ?? undefined,
      isAvailable: json.isAvailable ?? true,
    });
  }
  
  public static fromUnknownJSON(json: JSONType): WorkDocument | null | undefined {
    if (isNullOrUndefined(json)) return json;
    else if (isValidWorkDocumentJSON(json, { throw: true })) {
      return WorkDocument.fromJSON(json);
    }
    throw new Error("Cannot create WorkDocument from invalid JSON");
  }

  public readonly replacesId?: Guid;
  public readonly documentType?: WorkDocumentType;
  public readonly isAvailable: boolean = true;

  public get isEmpty(): boolean { return false }

  public constructor(values: WorkDocumentParams) {
    super(values);
    if (values.documentType) {
      this.documentType = values.documentType;
    }
    this.replacesId = values.replacesId;
    this.isAvailable = values.isAvailable ?? true;
  }

  public toJSON(): WorkDocumentJSON {
    const superJson = super.toJSON();
    return {
      ...superJson,
      creatorId: superJson.creatorId ?? undefined,
      hasSystemPermissions: Boolean(superJson.hasSystemPermissions),
      documentType: this.documentType,
      replacesId: this.replacesId?.value,
      isAvailable: this.isAvailable,
      fileSize: this.fileSize ?? 0,
      fileType: this.fileType
    };
  }

  public toString = (): string => this.name.toString() ?? "Untitled Document";

  public clone = (): WorkDocument => new WorkDocument(this);  

  public fromJSON = (json: JSONType): WorkDocument | null | undefined =>
    WorkDocument.isValidJSON(json, { throw: true }) ?
      WorkDocument.fromJSON(json) : undefined;
  
  protected static isValidJSON(
    json: JSONType,
    options?: { throw?: boolean; className?: string; }
  ): json is WorkDocumentJSON {
    if (isNullOrUndefined(json)) return true;
    const throwIfInvalid = options?.throw ?? false;
    const isValid = WorkDocumentSchema.WorkDocumentJSONZ.safeParse(json).success;
    
    if (!isValid && throwIfInvalid)
      throw new Error(
        `Cannot create ${options?.className ?? "WorkDocument"} from invalid document JSON`
      );
    
    return isValid;
  }
}

export class InvalidWorkDocumentJSON extends Error {
  public readonly json: JSONType;
  public readonly message: string = "Invalid WorkDocumentJSON";

  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.json = args.json;
  }
}

function isValidWorkDocumentJSON(
  json: JSONType, options?: { throw: boolean }
): json is WorkDocumentJSON {
  options = options ?? { throw: false };
  if (isNullOrUndefined(json)) return true;

  if (!WorkDocumentSchema.WorkDocumentJSONZ.safeParse(json).success) {
    if (options.throw) throw new InvalidWorkDocumentJSON({ json });
    return false;
  }

  return true;
}
